import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Scale, Calendar, CreditCard, UserX, Mail } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

export default function Terms() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';

  // SEO configuration for Terms page
  useSEO({
    title: isGerman ? 'AGB - Allgemeine Geschäftsbedingungen | ivoliq' : 'Terms and Conditions | ivoliq',
    description: isGerman ? 
      'Allgemeine Geschäftsbedingungen für die Nutzung von ivoliq - Buchhaltungssoftware für Freelancer und Selbstständige.' : 
      'Terms and Conditions for using ivoliq - accounting software for freelancers and self-employed professionals.',
    canonical: 'https://ivoliq.io/terms',
    robots: 'index,follow'
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link to="/" className="text-emerald-600 hover:text-emerald-700 mb-4 inline-block">
          ← {t('common.back')}
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {isGerman ? 'Allgemeine Geschäftsbedingungen (AGB)' : 'Terms and Conditions'}
        </h1>
        <p className="text-gray-600">
          {isGerman 
            ? 'Nutzungsbedingungen für ivoliq.io' 
            : 'Terms of Use for ivoliq.io'}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {isGerman ? 'Stand' : 'Last Updated'}: Juni 2026
        </p>
      </div>

      <div className="space-y-6">
        {/* 1. Geltungsbereich */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-emerald-600" />
              {isGerman ? '1. Geltungsbereich' : '1. Scope'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman 
                ? 'Diese Allgemeinen Geschäftsbedingungen (AGB) regeln die Nutzung der SaaS-Plattform ivoliq.io (nachfolgend "Plattform") durch Freiberufler, Selbstständige und Unternehmen (nachfolgend "Nutzer").'
                : 'These Terms and Conditions govern the use of the SaaS platform ivoliq.io (hereinafter "Platform") by freelancers, self-employed individuals and businesses (hereinafter "User").'}
            </p>
            
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 my-3">
              <p className="text-gray-800 text-sm font-semibold">
                {isGerman
                  ? '⚠️ Wichtig: Diese AGB richten sich ausschließlich an Unternehmer im Sinne des § 14 BGB bzw. § 1 UGB (Österreich). Verbraucher im Sinne des § 13 BGB sind von der Nutzung ausgeschlossen.'
                  : '⚠️ Important: These Terms are exclusively for business users as defined by § 14 BGB / § 1 UGB (Austria). Consumer use as defined by § 13 BGB is excluded.'}
              </p>
            </div>
            
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Die Plattform wird betrieben von der ivoliq GmbH (FN 680579 b, Handelsgericht Wien), vertreten durch den Geschäftsführer Wilhelm Wagner. Kontakt:'
                : 'The Platform is operated by ivoliq GmbH (FN 680579 b, Commercial Court Vienna), represented by its managing director Wilhelm Wagner. Contact:'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
              <li>E-Mail: admin@ivoliq.io</li>
              <li>{isGerman ? 'Weitere Details siehe' : 'Further details see'} <Link to="/impressum" className="text-emerald-600 hover:underline">Impressum</Link></li>
            </ul>
          </CardContent>
        </Card>

        {/* 2. Vertragsgegenstand */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              {isGerman ? '2. Vertragsgegenstand' : '2. Subject of Contract'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'ivoliq bietet eine cloudbasierte Software-as-a-Service (SaaS) Lösung für Freiberufler und Selbstständige zur Verwaltung von:'
                : 'ivoliq provides a cloud-based Software-as-a-Service (SaaS) solution for freelancers and self-employed individuals to manage:'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
              <li>{isGerman ? 'Beleg- und Dokumentenmanagement inkl. automatischer Texterkennung (OCR/KI)' : 'Receipt and document management incl. automatic text recognition (OCR/AI)'}</li>
              <li>{isGerman ? 'Rechnungserstellung und E-Mail-Versand (inkl. E-Rechnung/XRechnung)' : 'Invoice creation and email sending (incl. e-invoicing/XRechnung)'}</li>
              <li>{isGerman ? 'Einnahmen- und Ausgabenverwaltung inkl. Bankumsatz-Import (CSV)' : 'Income and expense management incl. bank transaction import (CSV)'}</li>
              <li>{isGerman ? 'Steuerberechnung und Umsatzsteuer-Voranmeldungs-Exporte (Deutschland, Österreich; Schweiz eingeschränkt)' : 'Tax calculation and VAT return exports (Germany, Austria; Switzerland limited)'}</li>
              <li>{isGerman ? 'Buchhaltungs-Exporte (DATEV, BMD, CSV) und Anlagenverwaltung' : 'Accounting exports (DATEV, BMD, CSV) and asset management'}</li>
              <li>{isGerman ? 'Zeiterfassung, Projekt- und Kundenverwaltung' : 'Time tracking, project and customer management'}</li>
              <li>{isGerman ? 'Optionale Zusammenarbeit mit Steuerberatern (siehe Abschnitt „Steuerberater-Anbindung")' : 'Optional collaboration with tax advisors (see section "Tax Advisor Connection")'}</li>
            </ul>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Der Leistungsumfang richtet sich nach dem gewählten Abonnement-Plan.'
                : 'The scope of services depends on the selected subscription plan.'}
            </p>
          </CardContent>
        </Card>

        {/* 3. Vertragsschluss und Registrierung */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '3. Vertragsschluss und Registrierung' : '3. Contract Formation and Registration'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Der Vertrag kommt durch Registrierung des Nutzers auf der Plattform zustande.'
                : 'The contract is formed upon user registration on the Platform.'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
              <li>{isGerman ? 'Registrierung via E-Mail/Passwort oder Google OAuth' : 'Registration via email/password or Google OAuth'}</li>
              <li>{isGerman ? 'Das Land wird automatisch bei Registrierung erkannt (DE/AT/CH)' : 'Country is automatically detected during registration (DE/AT/CH)'}</li>
              <li>{isGerman ? 'Nutzer müssen ihre Steuerresidenz bestätigen' : 'Users must confirm their tax residency'}</li>
              <li className="font-semibold">{isGerman ? 'Nur Unternehmer, Freiberufler und Selbstständige dürfen sich registrieren (B2B-only)' : 'Only businesses, freelancers and self-employed may register (B2B only)'}</li>
              <li>{isGerman ? 'Nur volljährige Personen dürfen sich registrieren' : 'Only persons of legal age may register'}</li>
            </ul>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Wird der Account durch eine Steuerberatungskanzlei für den Nutzer angelegt, kommt der Vertrag mit der Aktivierung des Accounts durch den Nutzer zustande (Festlegung des Passworts und ausdrückliche Zustimmung zu diesen AGB und der Datenschutzerklärung).'
                : 'If the account is created by a tax advisory firm on behalf of the user, the contract is formed upon activation of the account by the user (setting a password and expressly accepting these Terms and the Privacy Policy).'}
            </p>
          </CardContent>
        </Card>

        {/* 4. Abonnement-Pläne und Preise */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              {isGerman ? '4. Abonnement-Pläne und Preise' : '4. Subscription Plans and Prices'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '4.1 Verfügbare Pläne' : '4.1 Available Plans'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li><strong>Free:</strong> {isGerman ? '€0/Monat – 1 Kunde, 1 Projekt, keine Steuerberechnung' : '€0/month – 1 customer, 1 project, no tax calculation'}</li>
                <li><strong>Basic:</strong> {isGerman ? '€9/Monat oder €90/Jahr – 2 Kunden, 4 Projekte, automatische Rechnungserstellung' : '€9/month or €90/year – 2 customers, 4 projects, automatic invoicing'}</li>
                <li><strong>Pro:</strong> {isGerman ? '€19/Monat oder €190/Jahr – Unbegrenzte Kunden und Projekte, Steuerberechnung inkludiert' : '€19/month or €190/year – Unlimited customers and projects, tax calculation included'}</li>
                <li><strong>Lifetime-Bundle:</strong> {isGerman ? 'Einmalzahlung (€199 bzw. €249 je nach Verfügbarkeit, limitiert auf 150 Plätze) – Pro-Funktionsumfang ohne wiederkehrende Gebühren (siehe 4.4)' : 'One-time payment (€199 or €249 depending on availability, limited to 150 slots) – Pro feature set without recurring fees (see 4.4)'}</li>
                <li><strong>{isGerman ? 'Kanzlei-Lizenz' : 'Firm License'}:</strong> {isGerman ? 'Pro-Funktionsumfang, dessen Kosten von einer angebundenen Steuerberatungskanzlei getragen werden (siehe Abschnitt „Steuerberater-Anbindung")' : 'Pro feature set whose costs are borne by a connected tax advisory firm (see section "Tax Advisor Connection")'}</li>
              </ul>
              <p className="text-gray-700 text-sm mt-2">
                {isGerman
                  ? 'Alle Preise verstehen sich zzgl. der gesetzlichen Umsatzsteuer.'
                  : 'All prices are subject to applicable VAT.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '4.2 Zahlungsbedingungen' : '4.2 Payment Terms'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Die Abrechnung erfolgt je nach gewähltem Intervall monatlich oder jährlich im Voraus über den Zahlungsdienstleister Stripe. Das Lifetime-Bundle wird einmalig über Stripe abgerechnet. Bei Zahlungsverzug behält sich ivoliq das Recht vor, den Zugang vorübergehend zu sperren.'
                  : 'Billing is monthly or yearly in advance via the payment provider Stripe, depending on the selected interval. The Lifetime Bundle is billed once via Stripe. In case of payment delay, ivoliq reserves the right to temporarily suspend access.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '4.3 Upgrade und Downgrade' : '4.3 Upgrade and Downgrade'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Upgrades sind jederzeit möglich und werden sofort wirksam' : 'Upgrades are possible at any time and take effect immediately'}</li>
                <li>{isGerman ? 'Downgrades werden zum Ende der laufenden Abrechnungsperiode wirksam' : 'Downgrades take effect at the end of the current billing period'}</li>
                <li>{isGerman ? 'Bei Downgrade: Daten werden entsprechend der neuen Limits angepasst (älteste Einträge bleiben bestehen, neuere können gelöscht werden müssen)' : 'During downgrade: Data is adjusted according to new limits (oldest entries remain, newer ones may need to be deleted)'}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '4.4 Lifetime-Bundle' : '4.4 Lifetime Bundle'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Das Lifetime-Bundle gewährt nach einmaliger Zahlung dauerhaften Zugang zum Pro-Funktionsumfang — für die gesamte Betriebsdauer der Plattform, ohne wiederkehrende Gebühren' : 'After a one-time payment, the Lifetime Bundle grants permanent access to the Pro feature set — for the entire operating lifetime of the Platform, without recurring fees'}</li>
                <li>{isGerman ? '„Lifetime" bezieht sich auf die Lebensdauer des Produkts ivoliq, nicht auf die des Nutzers; bei dauerhafter Einstellung der Plattform erlischt der Zugang ohne Erstattungsanspruch, frühestens jedoch 12 Monate nach Kauf' : '"Lifetime" refers to the lifetime of the ivoliq product, not the user; if the Platform is permanently discontinued, access expires without refund entitlement, but no earlier than 12 months after purchase'}</li>
                <li>{isGerman ? 'Das Lifetime-Bundle ist nicht übertragbar und vom Umtausch ausgeschlossen; die Kulanzregelung gemäß 6.2 gilt entsprechend' : 'The Lifetime Bundle is non-transferable and excluded from exchange; the goodwill policy per 6.2 applies accordingly'}</li>
                <li>{isGerman ? 'Künftige Zusatzmodule, die über den Pro-Funktionsumfang hinausgehen, können gesondert bepreist werden' : 'Future add-on modules beyond the Pro feature set may be priced separately'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* 5. Vertragslaufzeit und Kündigung */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-600" />
              {isGerman ? '5. Vertragslaufzeit und Kündigung' : '5. Contract Duration and Termination'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.1 Laufzeit' : '5.1 Duration'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Der Vertrag läuft auf unbestimmte Zeit. Die Mindestlaufzeit beträgt bei bezahlten Plänen (Basic/Pro) die gewählte Abrechnungsperiode: einen Monat bei monatlicher, ein Jahr bei jährlicher Zahlweise. Bei Lifetime-Zugängen erfolgt eine einmalige Zahlung ohne wiederkehrende Laufzeit; das Nutzungsrecht besteht für die Betriebsdauer der Plattform.'
                  : 'The contract runs indefinitely. The minimum term for paid plans (Basic/Pro) is the selected billing period: one month for monthly, one year for annual billing. Lifetime access is a one-time payment without a recurring term; the right of use exists for the operating period of the Platform.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.2 Ordentliche Kündigung' : '5.2 Regular Termination'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Die Kündigung ist jederzeit zum Ende der laufenden Abrechnungsperiode (Monat bzw. Jahr) möglich' : 'Termination is possible at any time effective at the end of the current billing period (month or year)'}</li>
                <li>{isGerman ? 'Kündigung erfolgt über die Abonnementverwaltung in den Einstellungen (Stripe) oder per E-Mail an: support@ivoliq.io' : 'Termination via subscription management in settings (Stripe) or email to: support@ivoliq.io'}</li>
                <li>{isGerman ? 'Bis zum Ende der bezahlten Periode bleibt der volle Funktionsumfang erhalten' : 'Full functionality remains available until the end of the paid period'}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.3 Außerordentliche Kündigung' : '5.3 Extraordinary Termination'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Das Recht zur fristlosen Kündigung aus wichtigem Grund bleibt unberührt. Ein wichtiger Grund liegt insbesondere vor bei schwerwiegenden Verstößen gegen diese AGB oder geltendes Recht.'
                  : 'The right to immediate termination for good cause remains unaffected. Good cause exists particularly in case of serious violations of these Terms or applicable law.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '5.4 Folgen der Kündigung' : '5.4 Consequences of Termination'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Nach Vertragsende wird das Konto deaktiviert. Personenbezogene Nutzerdaten werden ohne Wiederherstellungsfrist gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen. Gesetzlich aufzubewahrende Buchhaltungs- und Rechnungsdaten bleiben hiervon unberührt.'
                  : 'After contract termination, the account is deactivated. Personal user data is deleted without a restoration period unless statutory retention obligations apply. Legally required accounting and invoice data remain unaffected.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 6. Rückerstattungen */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '6. Rückerstattungen' : '6. Refunds'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '6.1 Grundsatz' : '6.1 Principle'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Bereits gezahlte Abonnementgebühren werden grundsätzlich nicht erstattet, da die Leistung sofort erbracht wird (Zugang zur Plattform).'
                  : 'Subscription fees already paid are generally not refunded, as the service is provided immediately (platform access).'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '6.2 Ausnahmen' : '6.2 Exceptions'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Technische Ausfälle > 48 Stunden: Anteilige Erstattung nach Ermessen' : 'Technical outages > 48 hours: Pro-rata refund at discretion'}</li>
                <li>{isGerman ? 'Doppelabbuchung oder fehlerhafte Zahlungen: Volle Rückerstattung' : 'Double charges or erroneous payments: Full refund'}</li>
                <li>{isGerman ? 'Kulanzregelung innerhalb von 14 Tagen nach erstmaligem Upgrade: Rückerstattung möglich bei begründeter Anfrage an support@ivoliq.io' : 'Goodwill policy within 14 days after first upgrade: Refund possible upon justified request to support@ivoliq.io'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* 7. Account-Löschung */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-emerald-600" />
              {isGerman ? '7. Account-Löschung' : '7. Account Deletion'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '7.1 Löschung durch Nutzer' : '7.1 Deletion by User'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Nutzer können ihren Account jederzeit über die Einstellungen löschen. Hierzu ist eine Passwortbestätigung erforderlich.'
                  : 'Users can delete their account at any time via settings. Password confirmation is required.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '7.2 Ablauf bei Löschung' : '7.2 Deletion Process'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Automatische Kündigung des Stripe-Abonnements' : 'Automatic cancellation of Stripe subscription'}</li>
                <li>{isGerman ? 'Alle Nutzerdaten werden unwiderruflich gelöscht' : 'All user data is irrevocably deleted'}</li>
                <li>{isGerman ? 'Bestätigungs-E-Mail an die registrierte E-Mail-Adresse' : 'Confirmation email to registered email address'}</li>
                <li>{isGerman ? 'Keine Rückerstattung für laufenden Abrechnungszeitraum' : 'No refund for current billing period'}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '7.3 Aufbewahrung aus rechtlichen Gründen' : '7.3 Retention for Legal Reasons'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Rechnungsdaten und steuerrelevante Informationen werden gemäß den gesetzlichen Aufbewahrungsfristen gespeichert (§ 147 AO in Deutschland, z.B. 7 Jahre nach § 132 BAO in Österreich).'
                  : 'Invoice data and tax-relevant information are stored according to statutory retention periods (§ 147 AO in Germany, e.g., 7 years per § 132 BAO in Austria).'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 8. E-Mail-Versand */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-600" />
              {isGerman ? '8. E-Mail-Versand von Rechnungen' : '8. Email Sending of Invoices'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '8.1 Versandmethoden' : '8.1 Sending Methods'}</h3>
              <p className="text-gray-700 text-sm mb-3">
                {isGerman
                  ? 'ivoliq bietet zwei Methoden für den E-Mail-Versand von Rechnungen:'
                  : 'ivoliq offers two methods for email sending of invoices:'}
              </p>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>
                  <strong>{isGerman ? 'Standard-Versand über Brevo' : 'Standard sending via Brevo'}:</strong>{' '}
                  {isGerman 
                    ? 'Verfügbar für alle Pläne (Free, Basic, Pro). E-Mails werden über unseren Brevo-Account versendet.'
                    : 'Available for all plans (Free, Basic, Pro). Emails are sent via our Brevo account.'}
                </li>
                <li>
                  <strong>{isGerman ? 'Pro-Feature Gmail-Versand' : 'Pro Feature Gmail Sending'}:</strong>{' '}
                  {isGerman 
                    ? 'Nur für Pro-Plan Nutzer. E-Mails werden über dein eigenes Gmail-Konto versendet (OAuth 2.0).'
                    : 'Only for Pro plan users. Emails are sent via your own Gmail account (OAuth 2.0).'}
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '8.2 Versandlimits' : '8.2 Sending Limits'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li><strong>Free:</strong> {isGerman ? '5 E-Mails pro Monat' : '5 emails per month'}</li>
                <li><strong>Basic:</strong> {isGerman ? '50 E-Mails pro Monat' : '50 emails per month'}</li>
                <li><strong>Pro / Lifetime:</strong> {isGerman ? 'Unbegrenzt (Fair-Use-Vorbehalt: ausschließlich für legitime Geschäftszwecke)' : 'Unlimited (fair use: exclusively for legitimate business purposes)'}</li>
              </ul>
              <p className="text-gray-700 text-sm mt-2">
                {isGerman
                  ? 'Bei Überschreitung der Limits wird der E-Mail-Versand bis zum nächsten Monat gesperrt.'
                  : 'When limits are exceeded, email sending is blocked until the next month.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '8.3 Datenschutz und Verantwortlichkeit' : '8.3 Data Protection and Responsibility'}</h3>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-3">
                <p className="text-gray-800 text-sm">
                  <strong>{isGerman ? 'Wichtig' : 'Important'}:</strong>{' '}
                  {isGerman
                    ? 'Du bist als Nutzer der Verantwortliche i.S.d. DSGVO für die Kundendaten, die du per E-Mail versendest. ivoliq tritt als Auftragsverarbeiter auf. Stelle sicher, dass du berechtigt bist, die E-Mail-Adressen deiner Kunden zu verwenden.'
                    : 'As a user, you are the data controller under GDPR for the customer data you send via email. ivoliq acts as a data processor. Ensure you are authorized to use your customers\' email addresses.'}
                </p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '8.4 Kein Tracking' : '8.4 No Tracking'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'ivoliq verwendet bewusst KEINE E-Mail-Tracking-Technologien (keine Öffnungs- oder Klick-Verfolgung) zum Schutz der Privatsphäre deiner Kunden.'
                  : 'ivoliq deliberately does NOT use email tracking technologies (no open or click tracking) to protect your customers\' privacy.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '8.5 Technische Verfügbarkeit' : '8.5 Technical Availability'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Der E-Mail-Versand ist abhängig von der Verfügbarkeit der Drittanbieter (Brevo, Gmail). ivoliq übernimmt keine Garantie für die erfolgreiche Zustellung von E-Mails.'
                  : 'Email sending depends on the availability of third-party providers (Brevo, Gmail). ivoliq does not guarantee successful email delivery.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 9. Nutzerpflichten */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '9. Nutzerpflichten' : '9. User Obligations'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '9.1 Allgemeine Pflichten' : '9.1 General Obligations'}</h3>
              <p className="text-gray-700 text-sm mb-3">
                {isGerman
                  ? 'Der Nutzer verpflichtet sich:'
                  : 'The User agrees to:'}
              </p>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Wahrheitsgemäße Angaben bei der Registrierung zu machen' : 'Provide accurate information during registration'}</li>
                <li>{isGerman ? 'Zugangsdaten vertraulich zu behandeln' : 'Keep access credentials confidential'}</li>
                <li>{isGerman ? 'Die Plattform nicht missbräuchlich zu verwenden' : 'Not misuse the Platform'}</li>
                <li>{isGerman ? 'Keine rechtswidrigen Inhalte hochzuladen' : 'Not upload unlawful content'}</li>
                <li>{isGerman ? 'Bei Standortwechsel die Steuerresidenz zu aktualisieren' : 'Update tax residency upon relocation'}</li>
                <li>{isGerman ? 'Den E-Mail-Versand nur für legitime Geschäftszwecke zu nutzen' : 'Use email sending only for legitimate business purposes'}</li>
                <li>{isGerman ? 'Sicherzustellen, dass sie berechtigt sind, E-Mails an die angegebenen Empfänger zu senden' : 'Ensure they are authorized to send emails to the specified recipients'}</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '9.2 Geistiges Eigentum und Nutzungsbeschränkungen' : '9.2 Intellectual Property and Usage Restrictions'}</h3>
              <p className="text-gray-700 text-sm mb-3">
                {isGerman
                  ? 'Sämtliche Rechte an der Plattform, insbesondere Urheber-, Marken- und sonstige Schutzrechte an Software, Design, Konzeption und Inhalten, verbleiben bei der ivoliq GmbH. Dem Nutzer wird ein einfaches, nicht übertragbares Nutzungsrecht für die Vertragsdauer eingeräumt. Untersagt sind insbesondere:'
                  : 'All rights to the Platform, in particular copyrights, trademarks and other protective rights to software, design, conception and content, remain with ivoliq GmbH. The User is granted a simple, non-transferable right of use for the duration of the contract. The following are prohibited in particular:'}
              </p>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Reverse Engineering, Dekompilierung oder Disassemblierung, soweit gesetzlich nicht zwingend erlaubt' : 'Reverse engineering, decompilation or disassembly, unless mandatorily permitted by law'}</li>
                <li>{isGerman ? 'Das systematische Auslesen, Kopieren oder Extrahieren von Inhalten, Strukturen oder Funktionen der Plattform (Scraping)' : 'Systematic reading, copying or extraction of content, structures or functions of the Platform (scraping)'}</li>
                <li>{isGerman ? 'Die Nutzung der Plattform zur Entwicklung eines konkurrierenden Produkts unter Verwendung nicht öffentlich zugänglicher Informationen' : 'Using the Platform to develop a competing product using non-publicly available information'}</li>
                <li>{isGerman ? 'Die Weitergabe von Zugängen an Dritte' : 'Sharing access credentials with third parties'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* 10. Haftung */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '10. Haftung und Gewährleistung' : '10. Liability and Warranty'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '10.1 Haftungsbeschränkung' : '10.1 Limitation of Liability'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'ivoliq haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit. Bei leichter Fahrlässigkeit haftet ivoliq nur bei Verletzung wesentlicher Vertragspflichten (Kardinalpflichten). In diesem Fall ist die Haftung auf den vorhersehbaren, typischerweise eintretenden Schaden begrenzt.'
                  : 'ivoliq is fully liable for intent and gross negligence as well as for injury to life, body or health. For slight negligence, ivoliq is only liable for breach of essential contractual obligations. In this case, liability is limited to foreseeable, typically occurring damage.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '10.2 Keine Steuerberatung' : '10.2 No Tax Advice'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Die bereitgestellten Steuerberechnungen dienen lediglich als Orientierungshilfe und ersetzen keine professionelle Steuerberatung. Du bist selbst für die Richtigkeit deiner Steuererklärungen verantwortlich.'
                  : 'The provided tax calculations serve only as guidance and do not replace professional tax advice. You are solely responsible for the accuracy of tax returns.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '10.3 Verfügbarkeit' : '10.3 Availability'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'ivoliq strebt eine Verfügbarkeit von 99% an, kann diese jedoch nicht garantieren. Wartungsarbeiten werden nach Möglichkeit angekündigt.'
                  : 'ivoliq aims for 99% availability but cannot guarantee it. Maintenance work will be announced when possible.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 11. Datenschutz */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '11. Datenschutz' : '11. Data Protection'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Die Verarbeitung personenbezogener Daten erfolgt gemäß unserer'
                : 'The processing of personal data is carried out according to our'}{' '}
              <Link to="/privacy" className="text-emerald-600 hover:underline">
                {isGerman ? 'Datenschutzerklärung' : 'Privacy Policy'}
              </Link>
              {isGerman ? ', die Bestandteil dieser AGB ist.' : ', which is part of these Terms.'}
            </p>
          </CardContent>
        </Card>

        {/* 12. Änderungen der AGB */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '12. Änderungen der AGB' : '12. Changes to Terms'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'ivoliq behält sich das Recht vor, diese AGB zu ändern. Dabei gilt:'
                : 'ivoliq reserves the right to modify these Terms. The following applies:'}
            </p>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-2 ml-4">
              <li>
                <strong>{isGerman ? 'Nicht nachteilige Änderungen' : 'Non-detrimental changes'}:</strong>{' '}
                {isGerman
                  ? 'Änderungen, die den Nutzer nicht benachteiligen (z.B. neue oder erweiterte Funktionen, redaktionelle Klarstellungen, Anpassungen an geänderte Rechtslage), werden mit Veröffentlichung auf dieser Seite wirksam. Das jeweils aktuelle Stand-Datum ist oben ausgewiesen.'
                  : 'Changes that do not disadvantage the user (e.g., new or extended features, editorial clarifications, adjustments to changed legal requirements) take effect upon publication on this page. The current version date is shown above.'}
              </li>
              <li>
                <strong>{isGerman ? 'Wesentliche nachteilige Änderungen' : 'Material detrimental changes'}:</strong>{' '}
                {isGerman
                  ? 'Änderungen, die wesentliche Pflichten des Nutzers begründen oder Leistungen einschränken (insbesondere Preiserhöhungen oder Leistungskürzungen), werden mindestens 14 Tage vor Inkrafttreten in Textform oder durch einen deutlichen Hinweis in der Anwendung angekündigt.'
                  : 'Changes that impose material obligations on the user or restrict services (in particular price increases or service reductions) will be announced at least 14 days before taking effect, in text form or via a prominent notice within the application.'}
              </li>
              <li>
                <strong>{isGerman ? 'Zustimmungsfiktion' : 'Deemed consent'}:</strong>{' '}
                {isGerman
                  ? 'Widerspricht der Nutzer angekündigten Änderungen nicht bis zu deren Inkrafttreten oder nutzt er die Plattform danach weiter, gelten die Änderungen als angenommen. Auf diese Rechtsfolge wird in der Ankündigung gesondert hingewiesen.'
                  : 'If the user does not object to announced changes before they take effect, or continues to use the Platform thereafter, the changes are deemed accepted. This legal consequence will be explicitly pointed out in the announcement.'}
              </li>
              <li>
                <strong>{isGerman ? 'Widerspruchsrecht' : 'Right to object'}:</strong>{' '}
                {isGerman
                  ? 'Im Falle eines Widerspruchs kann der Nutzer das Vertragsverhältnis bis zum Inkrafttreten der Änderungen ordentlich kündigen; ivoliq kann das Vertragsverhältnis in diesem Fall zum Inkrafttretens-Zeitpunkt beenden. Bereits bezahlte Zeiträume bleiben unberührt.'
                  : 'In case of objection, the user may terminate the contract before the changes take effect; ivoliq may in this case end the contract as of the effective date. Periods already paid for remain unaffected.'}
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* 13. Schlussbestimmungen */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? '13. Schlussbestimmungen' : '13. Final Provisions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '13.1 Anwendbares Recht' : '13.1 Applicable Law'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Es gilt das Recht der Republik Österreich unter Ausschluss des UN-Kaufrechts und der Verweisungsnormen des internationalen Privatrechts.'
                  : 'The law of the Republic of Austria applies, excluding the UN Convention on Contracts for the International Sale of Goods and the conflict of laws rules of international private law.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '13.2 Gerichtsstand' : '13.2 Jurisdiction'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Gerichtsstand für alle Streitigkeiten ist Wien, Österreich, sofern der Nutzer Unternehmer im Sinne des § 1 UGB ist.'
                  : 'The place of jurisdiction for all disputes is Vienna, Austria, provided the User is an entrepreneur as defined by § 1 UGB.'}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? '13.3 Salvatorische Klausel' : '13.3 Severability Clause'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.'
                  : 'Should individual provisions of these Terms be or become invalid, the validity of the remaining provisions remains unaffected.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Steuerberater-Anbindung (Juni 2026) */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? 'Steuerberater-Anbindung und Kanzlei-Lizenz' : 'Tax Advisor Connection and Firm License'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'ivoliq ermöglicht eine optionale Anbindung an Steuerberatungs- und Buchhaltungskanzleien („Kanzlei"). Die Verbindung kommt zustande durch: (a) Einladung der Kanzlei durch den Nutzer, (b) Einladung des Nutzers durch die Kanzlei mit Annahme durch den Nutzer, oder (c) Anlage des Accounts durch die Kanzlei mit anschließender Aktivierung durch den Nutzer.'
                : 'ivoliq enables an optional connection to tax advisory and accounting firms ("Firm"). The connection is established by: (a) the user inviting the firm, (b) the firm inviting the user with acceptance by the user, or (c) account creation by the firm with subsequent activation by the user.'}
            </p>
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? 'Berechtigungen (Vollmachten)' : 'Permissions (Authorizations)'}</h3>
              <p className="text-gray-700 text-sm">
                {isGerman
                  ? 'Die Kanzlei erhält nur die Berechtigungen, die ihr erteilt wurden (z.B. Einsicht in Belege, Rechnungen, Steuerdaten oder eine Bearbeiten-Vollmacht). Bei Anlage des Accounts durch die Kanzlei wird der Nutzer in der Aktivierungs-E-Mail ausdrücklich über die erteilten Berechtigungen informiert. Der Nutzer kann sämtliche Berechtigungen jederzeit in den Einstellungen einschränken oder vollständig widerrufen und die Verbindung beenden.'
                  : 'The firm only receives the permissions granted to it (e.g., access to receipts, invoices, tax data, or an editing authorization). If the account is created by the firm, the user is expressly informed about the granted permissions in the activation email. The user can restrict or fully revoke all permissions at any time in the settings and terminate the connection.'}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-2">{isGerman ? 'Kanzlei-Lizenz (Kostenübernahme durch die Kanzlei)' : 'Firm License (Costs Borne by the Firm)'}</h3>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-4">
                <li>{isGerman ? 'Die Kanzlei kann die Kosten der Pro-Lizenz des Nutzers übernehmen; der Nutzer erhält dann den vollen Pro-Funktionsumfang ohne eigene Zahlungspflicht' : 'The firm may bear the costs of the user\'s Pro license; the user then receives the full Pro feature set without own payment obligation'}</li>
                <li>{isGerman ? 'Die Kanzlei-Lizenz endet mit der Beendigung der Verbindung (durch Nutzer oder Kanzlei) oder des Vertrags zwischen ivoliq und der Kanzlei' : 'The firm license ends upon termination of the connection (by user or firm) or of the contract between ivoliq and the firm'}</li>
                <li>{isGerman ? 'Nach dem Ende der Kanzlei-Lizenz erhält der Nutzer eine Übergangsfrist von 30 Tagen, um ein eigenes Abonnement abzuschließen; danach wird der Account auf den Free-Plan umgestellt (keine Datenlöschung)' : 'After the firm license ends, the user receives a 30-day transition period to take out their own subscription; afterwards, the account is switched to the Free plan (no data deletion)'}</li>
              </ul>
            </div>
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Für das Verhältnis zwischen ivoliq und der Kanzlei gelten der gesonderte ivoliq SB-Vertrag und der SB-Auftragsverarbeitungsvertrag.'
                : 'The separate ivoliq Tax Advisor Agreement and the Tax Advisor Data Processing Agreement apply to the relationship between ivoliq and the firm.'}{' '}
              <Link to="/steuerberater-agb" className="text-emerald-600 hover:underline">SB-Vertrag</Link> · <Link to="/steuerberater-avv" className="text-emerald-600 hover:underline">SB-AVV</Link>
            </p>
          </CardContent>
        </Card>

        {/* Belegverarbeitung */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? 'Belegverarbeitung und KI-Erkennung' : 'Receipt Processing and AI Recognition'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'ivoliq bietet eine Belegerkennungsfunktion, die hochgeladene Dokumente (PDF, Bilder) automatisch ausliest. Im Standard-Modus erfolgt dies lokal mittels OCR-Technologie. Pro-Nutzer können optional eine KI-gestützte Erkennung aktivieren, bei der Belegdaten an Drittanbieter (OpenAI, Anthropic) übermittelt werden. Die Aktivierung erfordert eine separate Einwilligung. Trotz fortschrittlicher Technologie übernimmt ivoliq keine Garantie für die Vollständigkeit oder Richtigkeit der extrahierten Daten. Der Nutzer ist verpflichtet, die erkannten Daten vor der steuerlichen Verwendung zu prüfen.'
                : 'ivoliq offers a receipt recognition feature that automatically reads uploaded documents (PDF, images). In standard mode, this is done locally using OCR technology. Pro users can optionally activate AI-powered recognition, where receipt data is transmitted to third-party providers (OpenAI, Anthropic). Activation requires separate consent. Despite advanced technology, ivoliq does not guarantee the completeness or accuracy of extracted data. The user is obligated to verify recognized data before tax-related use.'}
            </p>
          </CardContent>
        </Card>

        {/* Kontakt */}
        <Card>
          <CardHeader>
            <CardTitle>
              {isGerman ? 'Kontakt bei Fragen' : 'Contact for Questions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-gray-700 text-sm">
              {isGerman
                ? 'Bei Fragen zu diesen AGB wende dich bitte an:'
                : 'For questions regarding these Terms, please contact:'}
            </p>
            <ul className="text-gray-700 text-sm space-y-1">
              <li><strong>{isGerman ? 'Allgemeine Anfragen' : 'General Inquiries'}:</strong> info@ivoliq.io</li>
              <li><strong>Support:</strong> support@ivoliq.io</li>
              <li><strong>{isGerman ? 'Administration' : 'Administration'}:</strong> admin@ivoliq.io</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}